// src/pages/TicketList.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import socket from '../services/socket';
import { toast } from 'react-toastify';
import { Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { playTicketSound, playCommentSound } from '../utils/sound';
import { notify } from '../utils/notify';
import { absoluteTime, dateTime } from '../utils/relativeTime';
import { nombreCorto } from '../utils/nombreCorto';
import useHotkeys from '../hooks/useHotkeys';

// Orden fijo igual que el backend: VIP abiertos primero, luego cancelación
// abierta, luego el resto (se conserva el orden relativo). Se reaplica cuando
// los sockets insertan o mueven filas.
const rangoFijo = (t) => (t.vipPendiente ? 2 : 0) + (t.cancelacionPendiente ? 1 : 0);
const conPinnedArriba = (arr) => arr
    .map((t, i) => [t, i])
    .sort((a, b) => (rangoFijo(b[0]) - rangoFijo(a[0])) || (a[1] - b[1]))
    .map(([t]) => t);

/* ═══════════════════════════════════════════════════════════════════════════════
   INLINE STYLES — paste the <style> block into your global CSS or index.html
   ═══════════════════════════════════════════════════════════════════════════════ */
const INLINE_CSS = `
/* ── TicketList v2 ────────────────────────────────────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.tl-root {
  --tl-radius: 14px;
  --tl-card: var(--c-card);
  --tl-bg: var(--c-input-bg);
  --tl-border: var(--c-border);
  --tl-text-1: var(--c-text-1);
  --tl-text-2: var(--c-text-2);
  --tl-text-3: var(--c-text-3);
  --tl-text-4: var(--c-text-4);
  --tl-accent: var(--brand-700, #0a4aa8);
  --tl-accent-hover: var(--brand-800, #07357f);
  --tl-accent-light: #dbeafe;
  --tl-accent-ring: rgba(29, 78, 216, .12);
  --tl-accent-shadow: rgba(29, 78, 216, .25);
  --tl-success: #059669;
  --tl-success-light: #ecfdf5;
  --tl-warning: #d97706;
  --tl-warning-light: #fffbeb;
  --tl-danger: #dc2626;
  --tl-danger-light: #fef2f2;
  --tl-shadow-sm: 0 1px 2px rgba(0,0,0,.04), 0 1px 3px rgba(0,0,0,.06);
  --tl-shadow-md: 0 4px 12px rgba(0,0,0,.06), 0 1px 3px rgba(0,0,0,.04);
  --tl-shadow-hover: 0 8px 24px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  max-width: 1060px;
  margin: 0 auto;
  padding: 32px 24px 48px;
  color: var(--tl-text-1);
  -webkit-font-smoothing: antialiased;
}

/* ── Header ────────────────────────────────────────────────────────────────── */
.tl-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}
.tl-header h2 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: var(--tl-text-1);
  margin: 0;
}
.tl-header .tl-count {
  font-size: 13px;
  color: var(--tl-text-4);
  margin: 4px 0 0;
  font-weight: 500;
}
.tl-btn-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--tl-accent);
  color: #fff !important;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all .2s ease;
  text-decoration: none !important;
  box-shadow: 0 1px 3px var(--tl-accent-shadow);
}
.tl-btn-new:hover {
  background: var(--tl-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--tl-accent-shadow);
}
.tl-btn-new svg {
  width: 16px; height: 16px;
}

/* ── Filters ───────────────────────────────────────────────────────────────── */
.tl-filters {
  background: var(--tl-card);
  border-radius: var(--tl-radius);
  border: 1px solid var(--tl-border);
  box-shadow: var(--tl-shadow-sm);
  padding: 18px 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
  align-items: flex-end;
}
.tl-filter-group { display: flex; flex-direction: column; }
.tl-filter-group.search { flex: 1 1 220px; min-width: 200px; }
.tl-filter-group.select { flex: 0 1 148px; }
.tl-filter-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--tl-text-4);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}
.tl-filters .form-control,
.tl-filters .form-select {
  font-size: 13px;
  border-radius: 10px;
  border: 1px solid var(--tl-border);
  padding: 8px 12px;
  background: var(--tl-bg);
  transition: border-color .15s, box-shadow .15s;
}
.tl-filters .form-control:focus,
.tl-filters .form-select:focus {
  border-color: var(--tl-accent);
  box-shadow: 0 0 0 3px var(--tl-accent-ring);
  background: var(--tl-card);
}
.tl-search-btn {
  background: var(--tl-bg);
  border: 1px solid var(--tl-border);
  border-left: none;
  border-radius: 0 10px 10px 0 !important;
  font-size: 13px;
  color: var(--tl-text-3);
  padding: 8px 14px;
  transition: all .15s;
}
.tl-search-btn:hover {
  background: var(--tl-accent-light);
  color: var(--tl-accent);
  border-color: var(--tl-accent);
}

/* ── Reasignar huérfanos button ────────────────────────────────────────────── */
.tl-orphans-btn {
  background: transparent;
  border: 1px solid var(--tl-border);
  color: var(--tl-text-2);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all .15s ease;
  font-family: inherit;
}
.tl-orphans-btn:hover {
  background: var(--tl-accent-light);
  color: var(--tl-accent);
  border-color: var(--tl-accent);
}
.tl-orphans-btn svg { transition: transform .4s ease; }
.tl-orphans-btn:hover svg { transform: rotate(180deg); }
.tl-filters .input-group .form-control {
  border-radius: 10px 0 0 10px;
}

/* ── Ticket cards ──────────────────────────────────────────────────────────── */
.tl-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tl-ticket {
  background: var(--tl-card);
  border: 1px solid var(--tl-border);
  border-radius: var(--tl-radius);
  padding: 18px 22px;
  cursor: pointer;
  transition: all .22s cubic-bezier(.4,0,.2,1);
  box-shadow: var(--tl-shadow-sm);
  position: relative;
  overflow: hidden;
}
.tl-ticket::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 4px;
  border-radius: 14px 0 0 14px;
  transition: width .2s;
}
.tl-ticket[data-status="open"]::before        { background: #2563eb; }
.tl-ticket[data-status="in_progress"]::before  { background: #d97706; }
.tl-ticket[data-status="closed"]::before       { background: #059669; }

.tl-ticket:hover {
  border-color: var(--tl-accent-light);
  box-shadow: var(--tl-shadow-hover);
  transform: translateY(-2px);
}
.tl-ticket:hover::before { width: 5px; }
.tl-ticket:active { transform: translateY(0); box-shadow: var(--tl-shadow-md); }

.tl-ticket-body {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}
.tl-ticket-left { flex: 1; min-width: 0; }
.tl-ticket-right {
  flex-shrink: 0;
  text-align: right;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Chips row */
.tl-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  align-items: center;
}

/* Folio chip */
.tl-folio {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: var(--tl-accent);
  color: #fff;
  border-radius: 7px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .3px;
  font-variant-numeric: tabular-nums;
}

/* Status pill */
.tl-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1;
}
.tl-status .dot {
  width: 6px; height: 6px;
  border-radius: 50%;
}
.tl-status[data-s="open"]        { background: #eff6ff; color: #2563eb; }
.tl-status[data-s="open"] .dot   { background: #2563eb; }
.tl-status[data-s="in_progress"]        { background: #fffbeb; color: #d97706; }
.tl-status[data-s="in_progress"] .dot   { background: #d97706; }
/* Cerrado = "Resuelto ✓" en verde (cierre = resuelto, en positivo y coherente
   con el resto de la paleta donde verde = ok). */
.tl-status[data-s="closed"]        { background: #ecfdf5; color: #059669; }
.tl-status[data-s="closed"] .dot   { background: #059669; }
.tl-status .check { font-size: 11px; font-weight: 800; line-height: 1; }

/* Priority pill */
.tl-priority {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1;
}
.tl-priority[data-p="low"]    { background: #ecfdf5; color: #059669; }
.tl-priority[data-p="medium"] { background: #eff6ff; color: #2563eb; }
.tl-priority[data-p="high"]   { background: #fffbeb; color: #d97706; }
.tl-priority[data-p="urgent"] { background: #fef2f2; color: #dc2626; }

/* Cancelación: chip + fila resaltada mientras siga pendiente */
.tl-pin {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  background: #fee2e2;
  color: #b91c1c;
  letter-spacing: .2px;
}
.tl-ticket[data-pinned="1"] {
  border-color: #fca5a5;
  background: linear-gradient(90deg, rgba(220, 38, 38, .09) 0%, var(--tl-card) 55%);
}
.tl-ticket[data-pinned="1"]::before { background: #dc2626; width: 6px; }

/* VIP (solicitante con puesto de dirección): chip dorado + fila resaltada mientras siga abierto */
.tl-vip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 800;
  font-size: 11px;
  line-height: 1;
  background: #fef3c7;
  color: #92400e;
  letter-spacing: .4px;
}
.tl-ticket[data-vip="1"] {
  border-color: #fcd34d;
  background: linear-gradient(90deg, rgba(245, 158, 11, .12) 0%, var(--tl-card) 55%);
}
.tl-ticket[data-vip="1"]::before { background: #f59e0b; width: 6px; }

/* Fila seleccionada con el teclado (j / k) */
.tl-ticket.tl-selected {
  outline: 2px solid var(--tl-accent);
  outline-offset: 2px;
  box-shadow: var(--tl-shadow-hover);
}

/* Title */
.tl-title {
  font-weight: 700;
  font-size: 15px;
  color: var(--tl-text-1);
  margin: 0 0 8px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Meta row */
.tl-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 12px;
  color: var(--tl-text-3);
}
.tl-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.tl-meta-icon {
  width: 14px; height: 14px;
  opacity: .55;
  flex-shrink: 0;
}

/* Right side date info */
.tl-date-label {
  font-size: 11px;
  color: var(--tl-text-4);
  font-weight: 500;
}
.tl-date-value {
  font-size: 12px;
  color: var(--tl-text-2);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.tl-due {
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 2px;
}
.tl-due.overdue  { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.tl-due.warning  { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
.tl-due.healthy  { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
.tl-due .tl-due-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor; display: inline-block;
}

/* ── Flash animation ───────────────────────────────────────────────────────── */
@keyframes tl-flash {
  0%   { background: var(--tl-accent-light); border-color: var(--tl-accent); }
  100% { background: var(--tl-card); border-color: var(--tl-border); }
}
.tl-ticket.flash-bump {
  animation: tl-flash 1.6s ease-out;
}

/* ── Appear animation ──────────────────────────────────────────────────────── */
@keyframes tl-slideIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.tl-ticket {
  animation: tl-slideIn .35s ease both;
}
.tl-list .tl-ticket:nth-child(1)  { animation-delay: .02s; }
.tl-list .tl-ticket:nth-child(2)  { animation-delay: .05s; }
.tl-list .tl-ticket:nth-child(3)  { animation-delay: .08s; }
.tl-list .tl-ticket:nth-child(4)  { animation-delay: .11s; }
.tl-list .tl-ticket:nth-child(5)  { animation-delay: .14s; }
.tl-list .tl-ticket:nth-child(6)  { animation-delay: .17s; }
.tl-list .tl-ticket:nth-child(7)  { animation-delay: .20s; }
.tl-list .tl-ticket:nth-child(8)  { animation-delay: .23s; }
.tl-list .tl-ticket:nth-child(9)  { animation-delay: .26s; }
.tl-list .tl-ticket:nth-child(10) { animation-delay: .29s; }

/* ── Empty & error states ──────────────────────────────────────────────────── */
.tl-empty {
  text-align: center;
  padding: 80px 20px;
  color: var(--tl-text-4);
}
.tl-empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: .4;
}
.tl-empty p { font-size: 15px; margin: 0; }
.tl-empty-hint { font-size: 13px; color: var(--tl-text-4); margin-top: 6px !important; }
.tl-empty-action {
  margin-top: 16px;
  background: var(--tl-accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.tl-empty-action:hover { background: var(--tl-accent-hover); }

.tl-error {
  background: var(--tl-danger-light);
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px 20px;
  color: var(--tl-danger);
  font-size: 14px;
  font-weight: 500;
}

.tl-loading {
  text-align: center;
  padding: 80px 0;
}

/* Botón "Filtros" (solo móvil) */
.tl-filters-toggle { display: none; }

/* ── Responsive ────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .tl-root { padding: 16px 12px 36px; }
  .tl-ticket { padding: 14px 16px; }
  .tl-ticket-body { flex-direction: column; gap: 10px; }
  .tl-ticket-right { text-align: left; min-width: 0; flex-direction: row; gap: 12px; flex-wrap: wrap; }
  .tl-filters { padding: 12px 14px; gap: 10px; margin-bottom: 16px; }
  .tl-header h2 { font-size: 22px; }
  /* Título a 2 líneas en vez de cortarlo a una */
  .tl-title { white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  /* Búsqueda a todo lo ancho; el resto de filtros a 2 por fila y escondidos
     hasta tocar "Filtros" */
  .tl-filter-group.search.main { flex: 1 1 100%; min-width: 0; }
  .tl-filter-group { flex: 1 1 calc(50% - 5px) !important; min-width: 0; }
  .tl-filters:not(.open) .tl-filter-group:not(.main) { display: none; }
  .tl-filters-toggle {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    flex: 1 1 100%; padding: 9px 12px; border-radius: 10px;
    border: 1px solid var(--tl-border); background: var(--tl-bg); color: var(--tl-text-2);
    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
  }
  .tl-filters-toggle span { font-size: 10px; opacity: .7; }
  /* 16px evita que iOS haga zoom al enfocar */
  .tl-filters .form-control, .tl-filters .form-select { font-size: 16px; }
}
`;

// En pantallas táctiles (celular/tablet) el ticket se abre en la misma
// pestaña: abrir una nueva por cada ticket en el teléfono las acumula sin fin.
const IS_TOUCH = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

// ── Helpers ────────────────────────────────────────────────────────────────────
const STATUS_LABELS  = { open: 'Abierto', in_progress: 'En progreso', closed: 'Cerrado' };
const PRIORITY_LABELS = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };

// ── SVG icons (inline, tiny) ──────────────────────────────────────────────────
const IconFolder = () => (
    <svg className="tl-meta-icon" viewBox="0 0 20 20" fill="currentColor">
        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    </svg>
);
const IconUser = () => (
    <svg className="tl-meta-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
);
const IconWrench = () => (
    <svg className="tl-meta-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
);
const IconPlus = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" />
    </svg>
);
const IconSearch = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    </svg>
);
const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        <path d="M3 21v-5h5" />
    </svg>
);

// ── Banderas y nombres de países ─────────────────────────────────────────────
const COUNTRY_MAP = {
    'MEXICO':               { flag: '🇲🇽', label: 'México' },
    'MÉXICO':               { flag: '🇲🇽', label: 'México' },
    'GUATEMALA':            { flag: '🇬🇹', label: 'Guatemala' },
    'PERU':                 { flag: '🇵🇪', label: 'Perú' },
    'PERÚ':                 { flag: '🇵🇪', label: 'Perú' },
    'COLOMBIA':             { flag: '🇨🇴', label: 'Colombia' },
    'ARGENTINA':            { flag: '🇦🇷', label: 'Argentina' },
    'CHILE':                { flag: '🇨🇱', label: 'Chile' },
    'ECUADOR':              { flag: '🇪🇨', label: 'Ecuador' },
    'BOLIVIA':              { flag: '🇧🇴', label: 'Bolivia' },
    'VENEZUELA':            { flag: '🇻🇪', label: 'Venezuela' },
    'PARAGUAY':             { flag: '🇵🇾', label: 'Paraguay' },
    'URUGUAY':              { flag: '🇺🇾', label: 'Uruguay' },
    'COSTA RICA':           { flag: '🇨🇷', label: 'Costa Rica' },
    'PANAMA':               { flag: '🇵🇦', label: 'Panamá' },
    'PANAMÁ':               { flag: '🇵🇦', label: 'Panamá' },
    'HONDURAS':             { flag: '🇭🇳', label: 'Honduras' },
    'EL SALVADOR':          { flag: '🇸🇻', label: 'El Salvador' },
    'NICARAGUA':            { flag: '🇳🇮', label: 'Nicaragua' },
    'REPUBLICA DOMINICANA': { flag: '🇩🇴', label: 'Rep. Dominicana' },
    'REPÚBLICA DOMINICANA': { flag: '🇩🇴', label: 'Rep. Dominicana' },
    'CUBA':                 { flag: '🇨🇺', label: 'Cuba' },
};
function countryDisplay(pais) {
    if (!pais) return null;
    const entry = COUNTRY_MAP[pais.toUpperCase()];
    return entry
        ? { flag: entry.flag, label: entry.label }
        : { flag: '🌎', label: pais };
}

// ══════════════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════════════
export default function TicketList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [q,          setQ]          = useState('');
    const [status,     setStatus]     = useState(searchParams.get('status') || 'all');
    const [priority,   setPriority]   = useState('all');
    const [department, setDepartment] = useState('all');
    const [pais,       setPais]       = useState('all');
    const [mine,       setMine]       = useState('0');
    const [assignedTo, setAssignedTo] = useState('all'); // solo admin/leader
    const [agents,     setAgents]     = useState([]);    // poblado bajo demanda para el filtro
    const [sucursal,   setSucursal]   = useState('all'); // solo admin
    const [solicitante, setSolicitante] = useState('');  // solo admin
    const [reopened,   setReopened]   = useState(searchParams.get('reopened') === '1');
    // Móvil: los filtros (menos la búsqueda) viven detrás de un botón "Filtros"
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [page,       setPage]       = useState(1);
    const LIMIT = 20;

    const [tickets,    setTickets]    = useState([]);
    const [total,      setTotal]      = useState(undefined);
    const [depts,      setDepts]      = useState([]);
    const [paises,     setPaises]     = useState([]);
    const [sucursales, setSucursales] = useState([]); // solo admin

    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');
    const [flashId, setFlashId] = useState(null);
    const [selIdx,  setSelIdx]  = useState(-1);     // fila seleccionada con j / k
    const searchRef = useRef(null);

    const { user } = useContext(AuthContext);
    const navigate  = useNavigate();
    const isStaff   = user?.role === 'admin' || user?.role === 'agent' || user?.role === 'leader';

    // ── Atajos de la lista (ver components/ShortcutsHelp.jsx) ──────────────
    const totalPages   = typeof total === 'number' ? Math.ceil(total / LIMIT) : 1;
    const seleccionado = selIdx >= 0 ? tickets[selIdx] : null;
    const moverSeleccion = (delta) => {
        if (!tickets.length) return;
        setSelIdx(i => {
            const base = i < 0 ? (delta > 0 ? -1 : tickets.length) : i;
            const n = Math.max(0, Math.min(tickets.length - 1, base + delta));
            document.querySelector(`.tl-ticket[data-idx="${n}"]`)?.scrollIntoView({ block: 'nearest' });
            return n;
        });
    };
    // Enter solo cuando el foco no está en un botón o link (ahí Enter es suyo).
    const focoLibre = () => !['button', 'a'].includes((document.activeElement?.tagName || '').toLowerCase());
    useHotkeys([
        { keys: 'j',         run: () => moverSeleccion(1) },
        { keys: 'k',         run: () => moverSeleccion(-1) },
        { keys: 'arrowdown', run: () => moverSeleccion(1) },
        { keys: 'arrowup',   run: () => moverSeleccion(-1) },
        { keys: 'enter', run: () => navigate(`/tickets/${seleccionado._id}`), when: () => !!seleccionado && focoLibre() },
        { keys: 'o',     run: () => window.open(`/tickets/${seleccionado._id}`, '_blank', 'noopener'), when: () => !!seleccionado },
        { keys: 'm',     run: () => setMine(v => (v === '1' ? '0' : '1')), when: () => isStaff },
        { keys: 'f',     run: () => searchRef.current?.focus() },
        { keys: '[',     run: () => setPage(p => Math.max(1, p - 1)), when: () => page > 1 },
        { keys: ']',     run: () => setPage(p => p + 1),              when: () => page < totalPages },
    ]);
    // Si cambia la lista (filtros, página) la selección deja de tener sentido.
    useEffect(() => { setSelIdx(-1); }, [tickets.length, page]);

    const mineRef       = useRef(mine);
    const userIdRef     = useRef(user?.id);
    const myDeptsRef    = useRef((user?.departments || []).map(d => String(d._id || d)));
    const statusRef     = useRef(status);
    const priorityRef   = useRef(priority);
    const departmentRef = useRef(department);
    const paisRef       = useRef(pais);
    const qRef          = useRef(q);
    const assignedToRef  = useRef(assignedTo);
    const sucursalRef    = useRef(sucursal);
    const solicitanteRef = useRef(solicitante);
    const reopenedRef    = useRef(reopened);
    useEffect(() => { mineRef.current        = mine;       }, [mine]);
    useEffect(() => { userIdRef.current      = user?.id;   }, [user]);
    useEffect(() => { myDeptsRef.current     = (user?.departments || []).map(d => String(d._id || d)); }, [user]);
    useEffect(() => { statusRef.current      = status;     }, [status]);
    useEffect(() => { priorityRef.current    = priority;   }, [priority]);
    useEffect(() => { departmentRef.current  = department; }, [department]);
    useEffect(() => { paisRef.current        = pais;       }, [pais]);
    useEffect(() => { qRef.current           = q;          }, [q]);
    useEffect(() => { assignedToRef.current  = assignedTo; }, [assignedTo]);
    useEffect(() => { sucursalRef.current    = sucursal;   }, [sucursal]);
    useEffect(() => { solicitanteRef.current = solicitante;}, [solicitante]);
    useEffect(() => { reopenedRef.current    = reopened;   }, [reopened]);

    // Reset to page 1 when filters change
    useEffect(() => { setPage(1); }, [q, status, priority, department, pais, mine, reopened, assignedTo, sucursal, solicitante]);

    // Cargar agentes para el filtro — solo admin/leader (los demás no tienen acceso a /users/agents).
    useEffect(() => {
        if (user?.role !== 'admin' && user?.role !== 'leader') return;
        api.get('/users/agents')
            .then(r => setAgents(Array.isArray(r.data) ? r.data : []))
            .catch(() => setAgents([]));
    }, [user?.role]);

    // Cargar sucursales para el filtro — solo admin.
    useEffect(() => {
        if (user?.role !== 'admin') return;
        api.get('/tickets/sucursales')
            .then(r => setSucursales(Array.isArray(r.data) ? r.data : []))
            .catch(() => setSucursales([]));
    }, [user?.role]);

    const params = useMemo(
        () => ({
            q: q || undefined,
            status,
            priority,
            department,
            pais:        pais !== 'all'      ? pais       : undefined,
            mine,
            assignedTo:  assignedTo !== 'all' ? assignedTo : undefined,
            sucursal:    sucursal !== 'all'   ? sucursal   : undefined,
            solicitante: solicitante.trim()   || undefined,
            reopened: reopened ? '1' : undefined,
            page,
            limit: LIMIT,
        }),
        [q, status, priority, department, pais, mine, assignedTo, sucursal, solicitante, reopened, page],
    );

    const clearReopened = () => {
        setReopened(false);
        const sp = new URLSearchParams(searchParams);
        sp.delete('reopened');
        setSearchParams(sp, { replace: true });
    };

    /* ── Data fetching ──────────────────────────────────────────────────────── */
    const fetchTickets = async () => {
        setLoading(true); setError('');
        try {
            const res = await api.get('/tickets', { params });
            const arr = Array.isArray(res.data) ? res.data : (res.data.items || []);
            setTickets(arr);
            setTotal(Array.isArray(res.data) ? undefined : res.data.total);
            // Collect unique non-null paises from tickets when no country filter is active
            if (pais === 'all') {
                const found = [...new Set(arr.map(t => t.pais).filter(Boolean))].sort();
                if (found.length > 0) setPaises(prev => {
                    const merged = [...new Set([...prev, ...found])].sort();
                    return merged;
                });
            }
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudieron cargar los tickets');
        } finally { setLoading(false); }
    };

    useEffect(() => { api.get('/departments').then(r => setDepts(r.data || [])).catch(() => {}); }, []);
    useEffect(() => { api.get('/tickets/paises').then(r => setPaises(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchTickets(); }, [params]);

    /* ── Real-time events ───────────────────────────────────────────────────── */
    useEffect(() => {
        const flash = (id) => { setFlashId(String(id)); setTimeout(() => setFlashId(null), 1600); };

        const onCreated = (newTicket) => {
            // admin ve todos; agent/leader solo su departamento;
            // user y authorizer no reciben alerta de creación
            const role = user?.role;
            if (role === 'user' || role === 'authorizer') return;
            if (role === 'agent' || role === 'leader') {
                const ticketDept = String(newTicket.department?._id || newTicket.department || '');
                if (!myDeptsRef.current.includes(ticketDept)) return;
            }
            if (mineRef.current === '1') {
                const creatorId = String(newTicket.createdBy?._id || newTicket.createdBy);
                if (creatorId !== String(userIdRef.current)) return;
            }
            // Verificar que el ticket cumple los filtros activos antes de insertarlo
            if (statusRef.current !== 'all' && newTicket.status !== statusRef.current) return;
            if (priorityRef.current !== 'all' && newTicket.priority !== priorityRef.current) return;
            if (departmentRef.current !== 'all') {
                const tDept = String(newTicket.department?._id || newTicket.department || '');
                if (tDept !== String(departmentRef.current)) return;
            }
            if (paisRef.current !== 'all' && newTicket.pais !== paisRef.current) return;
            if (qRef.current) {
                const q = qRef.current.toLowerCase();
                const matchTitle = newTicket.title?.toLowerCase().includes(q);
                const matchFolio = String(newTicket.folio || '').includes(q);
                if (!matchTitle && !matchFolio) return;
            }
            // Filtro por agente (admin/leader): considera assignedTo, assignedTo2 y collaborators.
            if (assignedToRef.current && assignedToRef.current !== 'all') {
                const target = String(assignedToRef.current);
                const a1 = String(newTicket.assignedTo?._id  || newTicket.assignedTo  || '');
                const a2 = String(newTicket.assignedTo2?._id || newTicket.assignedTo2 || '');
                const collabs = (newTicket.collaborators || []).map(c => String(c?._id || c));
                if (a1 !== target && a2 !== target && !collabs.includes(target)) return;
            }
            // Filtro "solo reabiertos": un ticket recién creado nunca es reabierto.
            if (reopenedRef.current) return;
            setTickets(prev => {
                if (prev.some(t => String(t._id) === String(newTicket._id))) return prev;
                return conPinnedArriba([newTicket, ...prev]);
            });
            setTotal(prev => typeof prev === 'number' ? prev + 1 : prev);
            flash(newTicket._id);
            playTicketSound();
            const label = newTicket.folio ? `#${newTicket.folio}` : newTicket.title;
            toast.success(`Nuevo ticket: ${label}`, { autoClose: 3000 });
            notify('Nuevo ticket', `${label} — ${newTicket.createdBy?.name || 'alguien'}`);
        };

        const onBumped = ({ ticketId, ticket, by }) => {
            const role = user?.role;
            const uid  = String(user?.id || user?.userId || '');
            if (role === 'agent' || role === 'leader') {
                const ticketDept = String(ticket?.department?._id || ticket?.department || '');
                if (ticketDept && !myDeptsRef.current.includes(ticketDept)) return;
            } else if (role === 'authorizer') {
                // Solo tickets donde es autorizador asignado o donde es el creador
                const isMyAuth =
                    String(ticket?.authorizer?._id  || ticket?.authorizer  || '') === uid ||
                    String(ticket?.authorizer2?._id || ticket?.authorizer2 || '') === uid ||
                    String(ticket?.authorizer3?._id || ticket?.authorizer3 || '') === uid;
                const isCreator = String(ticket?.createdBy?._id || ticket?.createdBy || '') === uid;
                if (!isMyAuth && !isCreator) return;
            } else if (role === 'user') {
                const creatorId = String(ticket?.createdBy?._id || ticket?.createdBy || '');
                if (creatorId !== uid) return;
            }
            // Si hay filtro de agente activo, no reordenar tickets que no le pertenecen.
            if (assignedToRef.current && assignedToRef.current !== 'all') {
                const target = String(assignedToRef.current);
                const a1 = String(ticket?.assignedTo?._id  || ticket?.assignedTo  || '');
                const a2 = String(ticket?.assignedTo2?._id || ticket?.assignedTo2 || '');
                const collabs = (ticket?.collaborators || []).map(c => String(c?._id || c));
                if (a1 !== target && a2 !== target && !collabs.includes(target)) return;
            }
            // Solo notificar si el ticket está en la lista actual
            let found = false;
            setTickets(prev => {
                const idx = prev.findIndex(t => String(t._id) === String(ticketId));
                if (idx === -1) return prev;
                found = true;
                const updated = { ...prev[idx], ...ticket };
                return conPinnedArriba([updated, ...prev.filter((_, i) => i !== idx)]);
            });
            if (!found) return;
            flash(ticketId);
            playCommentSound();
            const folioOrTitle = ticket?.folio ? `#${ticket.folio}` : (ticket?.title || 'Ticket');
            toast.info(`Nuevo comentario en ${folioOrTitle} — por ${by || 'alguien'}`, { autoClose: 2500 });
            notify('Nuevo comentario', `${folioOrTitle} — ${by || 'alguien'}`);
        };

        socket.on('ticketCreated', onCreated);
        socket.on('ticketBumped',  onBumped);
        return () => { socket.off('ticketCreated', onCreated); socket.off('ticketBumped', onBumped); };
    }, []);

    /* ── Render ─────────────────────────────────────────────────────────────── */
    return (
        <>
            {/* Inject scoped styles */}
            <style>{INLINE_CSS}</style>

            <div className="tl-root">
                {/* ── Header ───────────────────────────────────────────────────────── */}
                <div className="tl-header">
                    <div>
                        <h2>Tickets</h2>
                        {typeof total === 'number' && (
                            <p className="tl-count">{total} resultado{total !== 1 ? 's' : ''}</p>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {(user?.role === 'admin' || user?.role === 'leader') && (
                            <button
                                type="button"
                                className="tl-orphans-btn"
                                onClick={async () => {
                                    if (!window.confirm('¿Reasignar tickets sin asignee creados en las últimas 48h? Solo aplica a deptos con auto-asignación.')) return;
                                    try {
                                        const { data } = await api.post('/tickets/sweep-orphans');
                                        toast.success(`Sweep OK: ${data.assigned} asignados, ${data.skipped} sin candidato (de ${data.scanned} escaneados)`);
                                    } catch (e) {
                                        toast.error(e.response?.data?.error || 'No se pudo ejecutar el sweep');
                                    }
                                }}
                                title="Dispara el cron de redistribución de tickets sin asignar (admin/leader)"
                            >
                                <IconRefresh /> Reasignar huérfanos
                            </button>
                        )}
                        <Link to="/tickets/new" className="tl-btn-new">
                            <IconPlus /> Nuevo ticket
                        </Link>
                    </div>
                </div>

                {/* ── Active filter chips ──────────────────────────────────────────── */}
                {reopened && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#fef3c7', color: '#92400e',
                            border: '1px solid #fcd34d', borderRadius: 999,
                            padding: '4px 12px', fontSize: 13, fontWeight: 600,
                        }}>
                            🔁 Solo tickets reabiertos
                            <button
                                type="button"
                                onClick={clearReopened}
                                aria-label="Quitar filtro"
                                style={{
                                    background: 'transparent', border: 'none', color: '#92400e',
                                    cursor: 'pointer', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: 0,
                                }}
                            >×</button>
                        </span>
                    </div>
                )}

                {/* ── Filters ──────────────────────────────────────────────────────── */}
                <div className={`tl-filters${filtersOpen ? ' open' : ''}`}>
                    <div className="tl-filter-group search main">
                        <span className="tl-filter-label">Buscar</span>
                        <InputGroup>
                            <Form.Control
                                ref={searchRef}
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                placeholder="Título, folio…"
                                onKeyDown={e => { if (e.key === 'Enter') fetchTickets(); }}
                            />
                            <Button variant="outline-secondary" className="tl-search-btn" onClick={fetchTickets}>
                                <IconSearch />
                            </Button>
                        </InputGroup>
                    </div>

                    {/* Solo móvil: muestra/oculta el resto de filtros */}
                    {(() => {
                        const n = [status !== 'all', priority !== 'all', department !== 'all', pais !== 'all',
                                   mine === '1', assignedTo !== 'all', sucursal !== 'all', !!solicitante].filter(Boolean).length;
                        return (
                            <button type="button" className="tl-filters-toggle" onClick={() => setFiltersOpen(v => !v)} aria-expanded={filtersOpen}>
                                Filtros{n ? ` · ${n} activo${n > 1 ? 's' : ''}` : ''} <span aria-hidden="true">{filtersOpen ? '▲' : '▼'}</span>
                            </button>
                        );
                    })()}

                    <div className="tl-filter-group select">
                        <span className="tl-filter-label">Estado</span>
                        <Form.Select value={status} onChange={e => setStatus(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="open">Abiertos</option>
                            <option value="in_progress">En progreso</option>
                            <option value="closed">Cerrados</option>
                        </Form.Select>
                    </div>

                    <div className="tl-filter-group select">
                        <span className="tl-filter-label">Prioridad</span>
                        <Form.Select value={priority} onChange={e => setPriority(e.target.value)}>
                            <option value="all">Todas</option>
                            <option value="low">Baja</option>
                            <option value="medium">Media</option>
                            <option value="high">Alta</option>
                            <option value="urgent">Urgente</option>
                        </Form.Select>
                    </div>

                    <div className="tl-filter-group select" style={{ flex: '0 1 180px' }}>
                        <span className="tl-filter-label">Departamento</span>
                        <Form.Select value={department} onChange={e => setDepartment(e.target.value)}>
                            <option value="all">Todos</option>
                            {(() => {
                                const restrict = user?.role === 'agent' || user?.role === 'leader';
                                const myIds = (user?.departments || []).map(d => String(d._id || d));
                                const visibleDepts = (restrict && myIds.length > 0)
                                    ? depts.filter(d => myIds.includes(String(d._id)))
                                    : depts;
                                return visibleDepts.map(d => <option key={d._id} value={d._id}>{d.name}</option>);
                            })()}
                        </Form.Select>
                    </div>

                    {isStaff && paises.length > 0 && (
                        <div className="tl-filter-group select">
                            <span className="tl-filter-label">País</span>
                            <Form.Select value={pais} onChange={e => setPais(e.target.value)}>
                                <option value="all">🌐 Todos</option>
                                {paises.map(p => {
                                    const c = countryDisplay(p);
                                    return <option key={p} value={p}>{c.flag} {c.label}</option>;
                                })}
                            </Form.Select>
                        </div>
                    )}

                    {isStaff && (
                        <div className="tl-filter-group select">
                            <span className="tl-filter-label">Mis tickets</span>
                            <Form.Select value={mine} onChange={e => setMine(e.target.value)}>
                                <option value="0">Todos</option>
                                <option value="1">Solo míos</option>
                            </Form.Select>
                        </div>
                    )}

                    {(user?.role === 'admin' || user?.role === 'leader') && (
                        <div className="tl-filter-group select" style={{ flex: '0 1 200px' }}>
                            <span className="tl-filter-label">Agente</span>
                            <Form.Select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                                <option value="all">Todos</option>
                                {agents.map(a => (
                                    <option key={a._id} value={a._id}>
                                        {a.name}{a.openTickets ? ` (${a.openTickets} abiertos)` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    {user?.role === 'admin' && sucursales.length > 0 && (
                        <div className="tl-filter-group select" style={{ flex: '0 1 180px' }}>
                            <span className="tl-filter-label">Sucursal</span>
                            <Form.Select value={sucursal} onChange={e => setSucursal(e.target.value)}>
                                <option value="all">Todas</option>
                                {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
                            </Form.Select>
                        </div>
                    )}

                    {user?.role === 'admin' && (
                        <div className="tl-filter-group search" style={{ flex: '0 1 200px' }}>
                            <span className="tl-filter-label">Solicitante</span>
                            <Form.Control
                                value={solicitante}
                                onChange={e => setSolicitante(e.target.value)}
                                placeholder="Nombre del solicitante…"
                                onKeyDown={e => { if (e.key === 'Enter') fetchTickets(); }}
                            />
                        </div>
                    )}
                </div>

                {/* ── List ─────────────────────────────────────────────────────────── */}
                {loading ? (
                    <div className="tl-loading"><Spinner animation="border" variant="primary" /></div>
                ) : error ? (
                    <div className="tl-error">{error}</div>
                ) : tickets.length === 0 ? (
                    (() => {
                        const hasFilters = q || status !== 'all' || priority !== 'all' ||
                                           department !== 'all' || pais !== 'all' || mine === '1' || reopened ||
                                           assignedTo !== 'all';
                        let icon = '📋';
                        let title = 'No hay tickets que mostrar.';
                        let hint = 'Crea uno nuevo para empezar.';
                        if (reopened) {
                            icon = '🔁';
                            title = 'Sin tickets reabiertos.';
                            hint = 'Buena noticia: nadie ha tenido que reabrir tickets.';
                        } else if (mine === '1' && status === 'open') {
                            icon = '🎉';
                            title = 'No tienes tickets abiertos.';
                            hint = 'Bandeja al día.';
                        } else if (mine === '1') {
                            icon = '👤';
                            title = 'No hay tickets asignados a ti con estos filtros.';
                            hint = 'Prueba ampliando el estado o quitando filtros.';
                        } else if (status === 'closed') {
                            icon = '✅';
                            title = 'Sin tickets cerrados en este filtro.';
                            hint = '';
                        } else if (q) {
                            icon = '🔍';
                            title = `Sin resultados para "${q}".`;
                            hint = 'Revisa la ortografía o prueba con otro término.';
                        } else if (hasFilters) {
                            icon = '🔎';
                            title = 'No hay tickets con estos filtros.';
                            hint = 'Prueba quitando alguno.';
                        }
                        const clearAll = () => {
                            setQ(''); setStatus('all'); setPriority('all');
                            setDepartment('all'); setPais('all'); setMine('0');
                            if (reopened) clearReopened();
                        };
                        return (
                            <div className="tl-empty">
                                <div className="tl-empty-icon">{icon}</div>
                                <p>{title}</p>
                                {hint && <p className="tl-empty-hint">{hint}</p>}
                                {hasFilters && (
                                    <button type="button" className="tl-empty-action" onClick={clearAll}>
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        );
                    })()
                ) : (
                    <>
                    <div className="tl-list">
                        {tickets.map((t, idx) => {
                            // Estado del SLA: red (vencido) | yellow (<24h) | green (>24h)
                            let slaState = null;
                            if (t.dueAt && t.status !== 'closed') {
                                const msLeft = new Date(t.dueAt).getTime() - Date.now();
                                if (msLeft < 0) slaState = 'overdue';
                                else if (msLeft < 24 * 3600 * 1000) slaState = 'warning';
                                else slaState = 'healthy';
                            }
                            return (
                                <a
                                    key={t._id}
                                    href={`/tickets/${t._id}`}
                                    target={IS_TOUCH ? undefined : '_blank'}
                                    rel="noopener noreferrer"
                                    className={`tl-ticket${flashId === String(t._id) ? ' flash-bump' : ''}${selIdx === idx ? ' tl-selected' : ''}`}
                                    data-status={t.status}
                                    data-idx={idx}
                                    data-pinned={t.cancelacionPendiente ? '1' : undefined}
                                    data-vip={t.vipPendiente ? '1' : undefined}
                                    style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div className="tl-ticket-body">
                                        {/* Left */}
                                        <div className="tl-ticket-left">
                                            <div className="tl-chips">
                                                <span className="tl-folio">#{t.folio || '—'}</span>
                                                <span className="tl-status" data-s={t.status}>
                                                    {t.status === 'closed'
                                                        ? <span className="check">✓</span>
                                                        : <span className="dot" />}
                                                    {t.status === 'closed' ? 'Resuelto' : (STATUS_LABELS[t.status] || t.status)}
                                                </span>
                                                <span className="tl-priority" data-p={t.priority}>
                                                    {PRIORITY_LABELS[t.priority] || t.priority}
                                                </span>
                                                {t.isVip && (
                                                    <span className="tl-vip" title={`Solicitante VIP${t.createdBy?.puesto ? `: ${t.createdBy.puesto}` : ''}`}>
                                                        ★ VIP
                                                    </span>
                                                )}
                                                {t.isCancelacion && (
                                                    <span className="tl-pin" title={t.cancelacionPendiente ? 'Cancelación pendiente de resolver' : 'Cancelación (resuelta)'}>
                                                        ⚠ Cancelación
                                                    </span>
                                                )}
                                            </div>
                                            <p className="tl-title">{t.title}</p>
                                            <div className="tl-meta">
                                                <span className="tl-meta-item"><IconFolder /> {t.department?.name || '—'}</span>
                                                <span className="tl-meta-item"><IconUser />   {t.createdBy?.name  || '—'}</span>
                                                {t.pais && (() => {
                                                    const c = countryDisplay(t.pais);
                                                    return <span className="tl-meta-item">{c.flag} {c.label}</span>;
                                                })()}
                                                {t.assignedTo?.name && (
                                                    <span className="tl-meta-item"><IconWrench /> {isStaff ? t.assignedTo.name : nombreCorto(t.assignedTo.name)}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right */}
                                        <div className="tl-ticket-right">
                                            {t.status === 'closed' && t.closedAt ? (
                                                <>
                                                    <span className="tl-date-label">Cerrado</span>
                                                    <span className="tl-date-value" title={absoluteTime(t.closedAt)}>{dateTime(t.closedAt)}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="tl-date-label">Últ. actividad</span>
                                                    <span className="tl-date-value" title={absoluteTime(t.lastActivityAt)}>
                                                        {t.lastActivityAt ? dateTime(t.lastActivityAt) : '—'}
                                                    </span>
                                                </>
                                            )}
                                            {slaState && (
                                                <span
                                                    className={`tl-due ${slaState}`}
                                                    title={`Vence ${absoluteTime(t.dueAt)}`}
                                                >
                                                    <span className="tl-due-dot" />
                                                    {slaState === 'overdue' ? 'Vencido' : `Vence ${dateTime(t.dueAt)}`}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                    {typeof total === 'number' && Math.ceil(total / LIMIT) > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                            <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Anterior</Button>
                            <span style={{ fontSize: 13, color: 'var(--tl-text-3)' }}>Página {page} de {Math.ceil(total / LIMIT)}</span>
                            <Button size="sm" variant="outline-secondary" disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>Siguiente →</Button>
                        </div>
                    )}
                    </>
                )}
            </div>
        </>
    );
}
